/* eslint @typescript-eslint/no-explicit-any: 0 */

import type { MutableRefObject } from 'react';
import React, { useRef, useState } from 'react';

import ChevronIcon from '~/assets/icons/Chevron';
import ProfileIcon from '~/assets/icons/Profile';
import StandardModal from '~/components/StandardModal';
import Tracked from '~/components/Tracked';
import useEnv from '~/hooks/useEnv';
import useIsSignedIn from '~/hooks/useIsSignedIn';
import type { ModalToggle } from '~/hooks/useModal';
import useNavigationLinkInteraction from '~/hooks/useNavigationLinkInteraction';
import useRegion from '~/hooks/useRegion';
import type { TrackedButtonClickName } from '~/services/gtm.client';

import type { MenuItemProps } from '../types';
import {
  ITEM_TEXT_CLASS_NAME,
  LINK_CLASS_NAME,
  MENU_LIST_ITEM_CLASS_NAME,
  menuListClassName,
  SECONDARY_LINK_CLASS_NAME,
  SUBMENU_TRIGGER_CLASS_NAME
} from './classNames';
import MobileMenuHeader from './MobileMenuHeader';
import MobileSubMenu from './MobileSubMenu';
import type { MobileMenuProps } from './types';

interface MobileMenuDrawerProps extends MobileMenuProps {
  menu: any;
  setClosed: ModalToggle;
}

export function AngleRight() {
  return (
    <span className="inline-block -rotate-90">
      <ChevronIcon width="14" height="14" stroke="black" />
    </span>
  );
}

export default function MobileMenuDrawer({
  menu,
  setClosed,
  setMobileMenuIsOpen,
  mobileMenuIsOpen
}: MobileMenuDrawerProps) {
  const region = useRegion();
  const [selectedItem, setSelectedItem] = useState<MenuItemProps | null>(null);
  const [idsStack, setIdsStack] = useState<Array<MenuItemProps>>([]);

  const mainNavRef: MutableRefObject<HTMLDivElement | null> = useRef(null);

  function closeMenu() {
    setClosed();
    setMobileMenuIsOpen(!mobileMenuIsOpen);
    setSelectedItem(null);
    setIdsStack([]);
  }

  function headerBack(currentItem: MenuItemProps) {
    if (!idsStack.length) return;

    const newStack = idsStack.filter((item) => item.name !== currentItem.name);
    if (!newStack.length) {
      setSelectedItem(null);
      setIdsStack([]);
    } else {
      const newItem = newStack[newStack.length - 1] ?? null;
      setSelectedItem(newItem);
      setIdsStack(newStack);
    }
  }

  function selectItem(item: MenuItemProps) {
    setSelectedItem(item);

    idsStack.push(item);
    setIdsStack(idsStack);
    if (mainNavRef.current) {
      mainNavRef.current!.scrollTo({ block: 'start', top: 0 } as any);
    }
  }

  const env = useEnv();
  const isSignedIn = useIsSignedIn();
  const fireNavigationLink = useNavigationLinkInteraction('phone_menu');

  const profileIconColor = isSignedIn ? '#56BD6F' : '#A9A9A9';
  const profilePage = `${env.SITE_BASE_URL}/${
    isSignedIn ? 'profile' : 'login'
  }`;

  const allItemsHaveIcons = menu.every((item: MenuItemProps) => !!item.icon);

  return (
    <StandardModal
      backdrop={mobileMenuIsOpen ? 'dim' : 'clear'}
      background="transparent"
      disableClickOutside
      hideClose
      isFullScreen
      isFullBody
      onCloseClick={setClosed}
      preventBodyScrollLock
    >
      <div
        aria-hidden="true"
        className="absolute left-0 top-0 z-[99] h-dvh w-full"
        onClick={closeMenu}
      />
      <nav
        id="aria-main-navigation"
        aria-label="Main navigation"
        className={`absolute top-0 z-[100] h-dvh w-[320px] overflow-x-hidden overscroll-none bg-white px-4 font-heading text-[14px] leading-[26px] text-dark-grey transition-transform duration-300 ease-in-out min-[576px]:w-[400px] min-[576px]:px-8 ${
          mobileMenuIsOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        ref={mainNavRef}
      >
        <MobileMenuHeader
          name={selectedItem ? selectedItem.name : ''}
          headerBack={headerBack}
          onClose={closeMenu}
          selectedItem={selectedItem}
          showBackButton={!!selectedItem}
        />
        <div className="relative">
          <ul
            className={menuListClassName({
              isExpanded: !!selectedItem,
              isShown: !selectedItem
            })}
          >
            {menu.map((menuItem: MenuItemProps, index: number) => (
              <React.Fragment key={menuItem.name}>
                {menuItem.type === 'filter' && (
                  <li className={MENU_LIST_ITEM_CLASS_NAME}>
                    <Tracked.ExternalLink
                      className={SECONDARY_LINK_CLASS_NAME}
                      href={menuItem.link || ''}
                      pagePosition="Header"
                      name={menuItem.name as TrackedButtonClickName}
                      variant="none"
                      onClick={() => {
                        fireNavigationLink(menuItem.link || '', index + 1, {
                          ancestorLabels: [],
                          label: menuItem.name
                        });
                        closeMenu();
                      }}
                    >
                      <div className={ITEM_TEXT_CLASS_NAME}>
                        {menuItem.name}
                      </div>
                    </Tracked.ExternalLink>
                  </li>
                )}
                {(!menuItem.children || !menuItem.children.length) && (
                  <li className={MENU_LIST_ITEM_CLASS_NAME}>
                    <Tracked.ExternalLink
                      className={LINK_CLASS_NAME}
                      href={menuItem.path}
                      pagePosition="Header"
                      name={menuItem.name as TrackedButtonClickName}
                      variant="none"
                      onClick={() => {
                        fireNavigationLink(menuItem.path, index + 1, {
                          ancestorLabels: [],
                          label: menuItem.name
                        });
                        closeMenu();
                      }}
                    >
                      <div className={ITEM_TEXT_CLASS_NAME}>
                        {menuItem.name}
                      </div>
                    </Tracked.ExternalLink>
                  </li>
                )}

                {!!menuItem.children && !!menuItem.children.length && (
                  <>
                    <li className={MENU_LIST_ITEM_CLASS_NAME}>
                      <button
                        type="button"
                        onClick={() => selectItem(menuItem)}
                        className={SUBMENU_TRIGGER_CLASS_NAME}
                      >
                        <div className={ITEM_TEXT_CLASS_NAME}>
                          {menuItem.name}
                        </div>
                        <span>
                          {!!menuItem.children.length && <AngleRight />}
                        </span>
                      </button>
                    </li>
                    <MobileSubMenu
                      ancestorLabels={[menuItem.name]}
                      selected={selectedItem}
                      parentName={menuItem.name}
                      closeMenu={closeMenu}
                      selectItem={selectItem}
                      menu={menuItem.children || []}
                      idsStack={idsStack}
                      path={menuItem.path || '/'}
                      nestingLevel={1}
                    />
                  </>
                )}
              </React.Fragment>
            ))}
            {region.code === 'GB' && (
              <li className={MENU_LIST_ITEM_CLASS_NAME}>
                <a
                  className={LINK_CLASS_NAME}
                  href={profilePage}
                  onClick={() => {
                    fireNavigationLink(profilePage, menu.length + 1, {
                      ancestorLabels: [],
                      label: 'My profile'
                    });
                    closeMenu();
                  }}
                >
                  {allItemsHaveIcons && (
                    <span className="mr-[5px]">
                      <ProfileIcon
                        width="25"
                        height="25"
                        color={profileIconColor}
                      />
                    </span>
                  )}
                  <div className={ITEM_TEXT_CLASS_NAME}>My profile</div>
                </a>
              </li>
            )}
          </ul>
        </div>
      </nav>
    </StandardModal>
  );
}
